import { Student } from './student';
import { Relation } from './relation';
import { DataSet, Node, Edge } from 'ngx-vis';

export interface AllStudentVisData {
    id?: number;
    label?: string;
    image?: string;
    shape?: string;
}

export class Group {
    private pGroupName: string;
    private pQuestion: string;
    private pStudents: Array<Student>;
    private pRelations: Array<Relation>;
    private pRelationsBuilt: boolean;
    private pAllRelations: Array<Relation>;

    constructor(name: string, question: string) {
        this.pGroupName = name;
        this.pQuestion = question;
        this.pRelationsBuilt = false;


        this.pStudents = new Array<Student>();
        this.pRelations = new Array<Relation>();
        this.pAllRelations = new Array<Relation>();
    }

    get question(): string {
        return this.pQuestion;
    }

    get group_name(): string {
        return this.pGroupName;
    }

    get students(): Array<Student> {
        return this.pStudents;
    }

    add_student(student: Student): void {
        console.log('   Adding student: ' + student.name + ' to group with selections: ' + student.selections);
        this.pStudents.push(student);
    }

    add_relation(relation: Relation): void {
        this.pRelations.push(relation);
    }

    get number_of_students(): number {
        return this.pStudents.length;
    }

    get number_of_relations(): number {
        return this.pRelations.length;
    }

    get_student_at_pos(pos): Student {
        if (pos < this.number_of_students) {
            return this.pStudents[pos];
        }
        return null;
    }

    get_student_id_from_name(name: string): number {
        for (const stud of this.pStudents) {
            if (stud.name === name) {
                return stud.id;
            }
        }

        return NaN;
    }

    is_matching_relation(relation1: Relation, relation2: Relation): boolean {
        if (relation1.relation_exists(relation2.from, relation2.to)) {
            return true;
        }
        return false;
    }

    set_selected_student(id, selectedByName): void {
        for (const student of this.pStudents) {
            student.set_selected_if_matching(id, selectedByName);
        }
    }

    build_relations(): void {
        console.log('**** Building Relations ****');
        if (!this.pRelationsBuilt) {
            console.log('Check ' + this.number_of_students + ' students for edges');

            for (const student of this.pStudents) {
                console.log('Checking relations for: ' + student.name);
                const studentSelections = student.selections;
                for (const selection of studentSelections) {
                    const rel = new Relation(student.id, this.get_student_id_from_name(selection));
                    this.pAllRelations.push(rel);
                    this.set_selected_student(rel.to, student.name);
                }
            }

            console.log('Found: ' + this.pAllRelations.length + ' relations');

            // Find matching relations
            for (const relation1 of this.pAllRelations) {
                for (const relation2 of this.pAllRelations.slice(1, ) ) {
                    if (!relation1.is_obsolete) {
                        if (this.is_matching_relation(relation1, relation2) ) {
                            relation1.set_matching_relation();
                            relation2.set_obsolete();
                        }
                    }
                }
            }

            // Copy all non obsolete relations to this._relations
            for (const relation of this.pAllRelations) {
                if (!relation.is_obsolete) {
                    relation.set_relation_id(this.number_of_relations);
                    this.add_relation(relation);
                }
            }

            console.log('Keeping: ' + this.pRelations.length + ' relations');

            this.pRelationsBuilt = true;
        }
    }

    get student_diagram_data(): object[] {
        const unsortedSudents = [...this.pStudents];
        unsortedSudents.sort( function(a: Student, b: Student): number {
            return b.number_of_selections - a.number_of_selections;
        });

        const studentNames: object[] = [];
        for (const student of unsortedSudents) {
            studentNames.push(student.diagram_data);
        }

        return studentNames;
    }

    get all_students_node_data(): DataSet<Node> {
        const studentDesc = new DataSet<Node>();
        for (const student of this.pStudents ) {
            studentDesc.add({id: student.id, label: student.name, image: this.get_img(student.is_selected), shape: 'image'});
        }
        return studentDesc;
    }

    get all_relations_edge_data(): DataSet<Edge> {
        const retVal = new DataSet<Edge>();
        for (const relation of this.pRelations) {
            retVal.add(relation.relation_node_data);
        }
        return retVal;
    }

    get_img(isSelected: boolean): string {
        if (isSelected) {
            return '../../assets/img/buhobox_uggla_sociogram.png';
        }
        else {
            return '../../assets/img/buhobox_uggla_sociogram_no_select.png';
        }
    }

    get matching_relations(): DataSet<Edge> {
        const retVal = new DataSet<Edge>();
        for (const relation of this.pRelations) {
            if (relation.is_matching_relation) {
                retVal.add(relation.relation_node_data);
            }
        }
        return retVal;
    }

    get single_relations(): DataSet<Edge> {
        const retVal = new DataSet<Edge>();
        for (const relation of this.pRelations) {
            if (!relation.is_matching_relation) {
                retVal.add(relation.relation_node_data);
            }
        }
        return retVal;
    }

    get all_relations_ids(): number[] {
        const retVal: number[] = [];
        for (const relation of this.pRelations) {
            retVal.push(relation.id);
        }
        return retVal;
    }

    get single_relations_ids(): number[] {
        const retVal: number[] = [];
        for (const relation of this.pRelations) {
            if (!relation.is_matching_relation) {
                retVal.push(relation.id);
            }
        }
        return retVal;
    }

    get matching_relations_ids(): number[] {
        const retVal: number[] = [];
        for (const relation of this.pRelations) {
            if (relation.is_matching_relation) {
                retVal.push(relation.id);
            }
        }
        return retVal;
    }

    get serialize_students(): string {
        const students = [];
        for (const student of this.pStudents) {
            students.push(student.serialize);
        }
        return JSON.stringify(students);
    }

    get serialize_relations(): string {
        const relations = [];
        for (const relation of this.pRelations) {
            relations.push(relation.serialize);
        }
        return JSON.stringify(relations);
    }

    get serialize(): string {
        return JSON.stringify({name: this.group_name, students: this.serialize_students,
            relations: this.serialize_relations, question: this.question});
    }

    deserialize(jsonAsString: string): void {
        console.log(jsonAsString);
        const json = JSON.parse(jsonAsString);
        console.log(jsonAsString);

        this.pGroupName = json['name'];
        this.pQuestion = json['question'];

        const students = JSON.parse(json['students']);
        console.log(students)
        for (const studentData of students) {
            if (studentData != null) {
                const student = new Student('', NaN);
                student.deserialize(studentData);
                this.add_student(student);
            }
        }

        const relations = JSON.parse(json['relations']);
        for (const relationData of relations) {
            if (relationData != null) {
                const relation = new Relation(NaN, NaN);
                relation.deserialize(relationData);
                this.add_relation(relation);
            }
        }
    }
}
